import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {FormControl} from "@angular/forms";
import {Product} from "../product";
import {Website} from "../website";
import {DialogComponent} from "../../../../ng2-bootstrap-modal/dialog.component";
import {DialogService} from "../../../../ng2-bootstrap-modal/dialog.service";
import {UserSubscription} from "../../../user/user-subscription";


import {Store} from '@ngrx/store';
import * as fromStore from '../../../store';
import {Observable, Subscription} from 'rxjs';
import {animate, style, transition, trigger} from "@angular/animations";
import {ActivityAction, IActivity} from '../../../activities/activity';
import {Config} from "../../../config/config";
import {MultisiteActionConfirmDialogComponent} from "../../../shared/components/dialogs/mulitiste-action-confirm-dialog/multisite-action-confirm-dialog.component";
import {TextUtils} from "../../../shared/utils/text-utils";

export interface IExtensionsData {
  website: Website;
  product: Product;
  parentProduct: Product;
  parentId: number;
  isManagable: boolean;
  scope: string;
  userSubscriptions: UserSubscription[];
  currentSubscription: UserSubscription;
}

@Component({
    selector: 'db-extensions',
    templateUrl: './extensions.component.html',
    styleUrls: ['./extensions.component.scss'],
    animations: [
        trigger('productItemAnimation', [
            transition("void => true", [
                style({
                    maxHeight: '0',
                    overflow: 'hidden'
                }),
                animate('500ms ease-out', style({
                    maxHeight: '200px',
                    overflow: 'hidden'
                })),
            ]),
            transition('true => void', [
                style({
                    maxHeight: '200px',
                    overflow: 'hidden'
                }),
                animate('500ms 1000ms ease-out', style({
                    maxHeight: '0',
                    overflow: 'hidden'
                })),
            ]),
        ]),
    ],
    standalone: false
})
export class ExtensionsComponent extends DialogComponent<IExtensionsData, null> implements OnInit, OnDestroy, IExtensionsData {

  public ActivityAction: any = ActivityAction;
  public ongoingBuilderActivity$: Observable<IActivity>;
  public booleanLoading: boolean;

  @Input() website: Website;
  @Input() product: Product;
  @Input() parentProduct: Product;
  @Input() isManagable: boolean;
  @Input() parentId: number;
  @Input() scope: string;

  @Input() userSubscriptions: UserSubscription[];
  @Input() currentSubscription: UserSubscription;
  public loading: boolean;

  public title: string;
  public changes: any;
  public websiteId: number;

  public filterBy: FormControl;
  public selectAll: FormControl;
  public bulkAction: FormControl;
  private subscriptions: Subscription[];
  public addOns: Product[];

  public plugins: Product[];

  public updatingProductsList: boolean;

  constructor(
    private _dialogService: DialogService,
    private _store: Store<fromStore.DbStoreState>

  ) {
    super(
      _dialogService
    );
  }

  ngOnInit() {
    this.filterBy = new FormControl('active');
    this.selectAll = new FormControl('');
    this.bulkAction = new FormControl('');
    this.title = `${this.product.title} changelog`;
    this.websiteId = this.website.id;

    this.ongoingBuilderActivity$ = this._store.select(fromStore.getBuilderOngoingActivity(this.website.id, Config.builderPluginSlug, !!this.website.domain_hash));
    this.ongoingBuilderActivity$.subscribe((activity) => {
      if (activity)  {
        if (activity.status === 'done' || activity.status === 'failed') {
          this.booleanLoading = true;
          setTimeout((): void => {
            this.booleanLoading = false;
          }, 1500);
        }
      }
    });

    this.addEventHandlers();
  }

  public onItemToggled(): void {
    this.updateSelectAllState();
  }

  private addEventHandlers(): void {
    this.subscriptions = [];

    this.subscriptions.push(this._store.select(fromStore.getWebsiteAndTenWebPluginExtensions(this.website.id, this.parentId)).subscribe((addons: Product[]) => {
      this.addOns = addons;
    }));
  }


  override ngOnDestroy(): void {
    this.removeEventHandlers();
  }

  private removeEventHandlers(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription: Subscription): void => {
        subscription.unsubscribe();
      });
    }
    this.subscriptions = null;
  }


  public applyBulkAction(bulkaction): void {
    let network = 0;
    const action = bulkaction ? bulkaction : this.bulkAction.value;
    if (this.website.isNetwork || (!this.website.isNetwork && this.website.parentId)) {
      this._dialogService
        .addDialog(MultisiteActionConfirmDialogComponent, {
          header: TextUtils.getText(action === ActivityAction.DEACTIVATE ? 'text_multisiteDeactivate.header' : 'text_multisiteActivate.header'),
          message: TextUtils.getText(action === ActivityAction.DEACTIVATE ? 'text_multisiteDeactivate.message' : 'text_multisiteActivate.message'),
          singleSiteButtonText: TextUtils.getText(action === ActivityAction.DEACTIVATE ? 'text_deactivate' : 'text_activate'),
          multisiteButtonText: TextUtils.getText(action === ActivityAction.DEACTIVATE ? 'text_networkDeactivate' : 'text_networkActivate'),
        })
        .subscribe((confirmed: string|boolean) => {
          if (confirmed) {
            if (confirmed === 'network') {
              network = 1;
            }
            this.dispatchActions(network, bulkaction);
          }
        });
    }
    else {
      this.dispatchActions(network, bulkaction);
    }

    this.toggleAllItemsSelected(false);
    this.updateSelectAllState();
  }


  private dispatchActions(network, bulkaction) {
    const bulkActionItems: Product[] = this.getBulkActionItems(bulkaction);
    if (bulkaction) {
      this._store.dispatch(new fromStore.InitWebsiteProductAction({
        website: this.website,
        products: bulkActionItems,
        action: bulkaction,
        network: network
      }));
    }
    else {
      if (this.bulkAction.value && bulkActionItems.length) {
        this._store.dispatch(new fromStore.InitWebsiteProductAction({
          website: this.website,
          products: bulkActionItems,
          action: this.bulkAction.value,
          network: network
        }));
      }
    }
  }

  private getBulkActionItems(bulkaction): Product[] {
    const bulkAction = bulkaction ? bulkaction : this.bulkAction.value;
    return this.addOns.reduce((bulkActionItems: any[], plugin: Product): number[] => {
      if (plugin.selected && plugin.canApplyAction(bulkAction)) {
        bulkActionItems.push(plugin);
      }
      return bulkActionItems;
    }, []);
  }

  private updateSelectAllState(): void {
    this.selectAll.setValue(!this.addOns.find((plugin: Product): boolean => !plugin.selected && !plugin.installed));
  }

  public toggleAllItemsSelected(selected: boolean): void {
    this.addOns.forEach((addOn: Product): void => {
      if (!addOn.installed) {
        addOn.selected = selected;
      }
    });
  }

  public update(): void {
    this._store.dispatch(new fromStore.InitWebsiteProductAction({
      website: this.website,
      products: [this.product],
      action: ActivityAction.UPDATE,
      network: 1
    }));
    this.close();
  }

  public get selectedAddOns(): Product[] {
    if (!this.addOns) {
      return [];
    }

    return this.addOns.filter((addOn: Product): boolean => {
      return addOn.selected;
    });
  }

  public productTrackBy(index: number, product: Product) {
    return product.uid;
  }

}
